import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CoreModule } from '../../core.module';

import { IBook, OrderPromoStacks, IWhereAvailable, IPurchaserInfo } from '../../../types/datatypes';
import { OrderPromoStackFeatureUpdate } from '../../../types/local';
import { PromoStack, PromoStackPopulated } from '../../../types/promo-stacks';
import { OrderPromoStackResult } from '../../../types/promo-stacks/order-promo-stack-result.interface';
import { ServerConnectionService } from '../server-connection.service';
import { IAPIResponse } from '../../../types/api-response.interface';
import { OrderPromoStackWithPromoStackAPIResponse } from '../../../types/responses';

export type FeatureData = {
	ASIN: string;
	bookPromoPrice: number;
	description: string;
	kindleCountdownDeal: boolean;
	kindleUnlimited: boolean;
	purchaserInfo: IPurchaserInfo;
	purchaseSource: string;
	whereAvailable: IWhereAvailable;
	subscribeToList: boolean;
	referrer: string;
	_owner?: string;
}

export type PromoStackAvailability = {
	pubDate: number;
	soldOut: boolean;
	available: number;
	siteAdvertId: string;
}

export type PromoStackPriceBucket = 'Free' | '$.99' | '$1.99' | '$2.99' | '$3.00+';

export type Stats = {
	genres: {
		id: string;
		name: string;
		count: number;
	}[];
	durations: {
		days: number;
		count: number;
	}[];
	prices: {
		price: PromoStackPriceBucket;
		count: number;
	}[];
	sites: {
		id: string;
		name: string;
		count: number;
	}[];
};

export type PromoStacksAPIResponse = IAPIResponse & { promoStacks: PromoStackPopulated[] };

export type PromoStackAvailabilityAPIResponse = IAPIResponse & { availability: PromoStackAvailability[][] };

export interface IPromoStackCountsResponse {
	stats: Stats;
}

export type PromoStackSearchSortField = 'name' | 'releaseDuration' | 'audience' | 'discountedPrice';

export type PromoStackSearchSort = {
	field: PromoStackSearchSortField;
	order: 'asc' | 'desc';
};

export type PromoStackSearchParams = {
	q: string;
	genres: string[];
	durations: number[];
	prices: PromoStackPriceBucket[];
	sites: string[];
	sort: PromoStackSearchSort;
	page: number;
	per: number;
};

export type PromoStackSearchSiteAdvert = {
	_id: string;
	name: string;
	isSeries?: boolean;
	rawReach?: number;
	_site: {
		_id: string;
		name: string;
	};
};

export type PromoStackSearchItem = {
	_id: string;
	name: string;
	description: string;
	releaseDuration: number;
	price: number;
	discountedPrice: number;
	audience: string;
	bookPriceRange?: {
		min?: number;
		max?: number;
	};
	featureReleaseSchedules: {
		day: number;
		siteAdvertId: string;
	}[][];
	siteAdverts: PromoStackSearchSiteAdvert[];
	headerImage?: string;
	headerHTML?: string;
	footerHTML?: string;
	newSlug?: string;
};

export interface IPromoStackSearchResponse {
	promoStacks: PromoStackSearchItem[];
	pagination: {
		page: number;
		limit: number;
		totalPages: number;
		totalDocs: number;
	};
}

@Injectable({
	providedIn: CoreModule,
})

export class PromoStackService {
	public statuses: ['in-progress', 'public', 'archived'];

	private _promoStackStore = <ReplaySubject<Object>> new ReplaySubject(1);

	private dataStore = {
		store: [],
	};

	constructor(
		private serverConnectionService: ServerConnectionService,
	) { }

	get promoStackStore$() {
		return this._promoStackStore.asObservable();
	}

	public getPromoStacks() {
		this.serverConnectionService.get$<PromoStacksAPIResponse>('/api/promostacks/list-available').pipe(
			map((response) => (response.success ? response.promoStacks : [])),
			catchError(() => of<PromoStackPopulated[]>([])),
		).subscribe((promoStacks) => {
			this.dataStore.store = promoStacks;
			this._promoStackStore.next(this.dataStore.store);
		});
	}

	public getPromoStackBySlug(slug: string): PromoStackPopulated {
		return this.dataStore.store.find((promoStack: PromoStack) => this._kabobCase(promoStack.name) === slug || promoStack.newSlug === slug);
	}

	public getPromoStackAvailability(id: string): Observable<PromoStackAvailability[][]> {
		return this.serverConnectionService.get$<PromoStackAvailabilityAPIResponse>(`/api/promostacks/availability/${id}`).pipe(
			map((response) => (response.success ? response.availability : [])),
			catchError(() => of<PromoStackAvailability[][]>([])),
		);
	}

	public fetchOrderPromoStack(id: string) {
		return this.serverConnectionService.get$<OrderPromoStackWithPromoStackAPIResponse>(`/api/order-promo-stacks/${id}`);
	}

	public createPromoStack(
		promoStackId: string,
		orderId: string,
		bookData: IBook,
		featureData: FeatureData,
		selectedDateSet: PromoStackAvailability[],
	) {
		return new Promise<OrderPromoStacks>((resolve, reject) => this.serverConnectionService.post$(
			'/api/order-promo-stacks/create', { promoStackId, orderId, bookData, featureData, selectedDateSet },
		).subscribe({
			next: (data: OrderPromoStackResult) => resolve(data.orderPromoStacks),
			error: () => reject(),
		}));
	}

	public updatePromoStack(
		id: String,
		features: any[],
		bookId: string,
		startDate: string,
		dates: any[],
		paymentAmount: number,
		bookPriceDuringPromo: number,
	) {
		const data = { id, features, bookId, startDate, dates, paymentAmount, bookPriceDuringPromo };
		return new Promise<OrderPromoStacks>((resolve, reject) => {
			this.serverConnectionService.post$('/api/order-promo-stacks/update', data)
				.subscribe((result: OrderPromoStackResult) => resolve(result.orderPromoStacks), reject);
		});
	}

	public getFilterStats() {
		return this.serverConnectionService.get$<IAPIResponse & { stats: Stats }>('/api/promostacks/stats/counts', { memberRole: 'gold' });
	}

	public searchPromoStacks({ q, genres, durations, prices, sites, sort, page, per }: PromoStackSearchParams) {
		const query: Record<string, string> = {
			memberRole: 'gold',
			sort: sort.field,
			order: sort.order,
			page: `${page}`,
			per: `${per}`,
		};
		const lists = { genres, durations, prices, sites };
		Object.entries(lists)
			.filter(([, values]) => values.length > 0)
			.forEach(([key, values]) => { query[key] = values.join(','); });

		const trimmed = q.trim();
		if (trimmed) query.q = trimmed;

		return this.serverConnectionService.get$<IAPIResponse & IPromoStackSearchResponse>('/api/promostacks/stats/search', query);
	}

	public updateFeaturePricing(featureId: string, newPrice: number) {
		return new Promise<void>((resolve, reject) => {
			this.serverConnectionService.post$<IAPIResponse>(
				`/api/order-promo-stacks/update-feature-payment/${featureId}`,
				{ paymentAmount: newPrice },
			).subscribe({
				next: (response) => {
					if (!response.success) {
						reject();
					}
					resolve();
				},
				error: () => {
					reject();
				},
			});
		});
	}

	public updatePromoStackFeatures(updates: OrderPromoStackFeatureUpdate) {
		return new Promise<OrderPromoStacks>((resolve, reject) => {
			this.serverConnectionService
				.post$('/api/order-promo-stacks/update-order-features', updates)
				.subscribe((result: OrderPromoStackResult) => resolve(result.orderPromoStacks), reject);
		});
	}

	public updatePromoStackDates(updates: Partial<OrderPromoStackFeatureUpdate>) {
		return this.serverConnectionService.post$('/api/order-promo-stacks/update-dates', updates);
	}

	private _kabobCase(str: string) {
		return str.toLowerCase().replace(/ /g, '-');
	}
}
